import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';

export const MAX_BULK_SELECTION = 20;

export type BulkSelectionContextType = {
  isSelectModeActive: boolean;
  enterSelectMode: () => void;
  exitSelectMode: () => void;
  selectedChannelSids: string[];
  toggleChannelSelected: (channelSid: string) => void;
  clearSelection: () => void;
  isMaxSelectionReached: boolean;
};

export type BulkSelectionProviderProps = {
  children: ReactNode;
};

const defaultState: BulkSelectionContextType = {
  isSelectModeActive: false,
  enterSelectMode: () => {},
  exitSelectMode: () => {},
  selectedChannelSids: [],
  toggleChannelSelected: () => {},
  clearSelection: () => {},
  isMaxSelectionReached: false,
};

const BulkSelectionContext =
  createContext<BulkSelectionContextType>(defaultState);

export const BulkSelectionProvider = ({
  children,
}: BulkSelectionProviderProps) => {
  const [isSelectModeActive, setIsSelectModeActive] = useState(false);
  const [selectedChannelSids, setSelectedChannelSids] = useState<string[]>([]);

  const clearSelection = () => setSelectedChannelSids([]);

  const enterSelectMode = () => setIsSelectModeActive(true);

  const exitSelectMode = () => {
    setIsSelectModeActive(false);
    clearSelection();
  };

  const toggleChannelSelected = (channelSid: string) => {
    setSelectedChannelSids((current) => {
      if (current.includes(channelSid)) {
        return current.filter((sid) => sid !== channelSid);
      }
      if (current.length >= MAX_BULK_SELECTION) {
        return current;
      }
      return [...current, channelSid];
    });
  };

  const isMaxSelectionReached = useMemo(
    () => selectedChannelSids.length >= MAX_BULK_SELECTION,
    [selectedChannelSids],
  );

  return (
    <BulkSelectionContext.Provider
      value={{
        isSelectModeActive,
        enterSelectMode,
        exitSelectMode,
        selectedChannelSids,
        toggleChannelSelected,
        clearSelection,
        isMaxSelectionReached,
      }}
    >
      {children}
    </BulkSelectionContext.Provider>
  );
};

export const useBulkSelection = () => {
  const context = useContext(BulkSelectionContext);

  if (context === undefined) {
    throw new Error(`useBulkSelection must be used within a BulkSelectionProvider`);
  }

  return context;
};
